import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listAssignees, updateAssignee, type Assignee } from '../lib/assignees';
import {
  Alert,
  Badge,
  Button,
  CountBadge,
  EmptyState,
  LinkButton,
  PageHeader,
  SkeletonList,
} from '../components/ui';
import { PeopleIcon, PlusIcon } from '../components/icons';
import { useToast } from '../components/toast';
import { Avatar } from '../components/tasks/TaskBadges';
import { StarButton } from '../components/assignees/StarButton';

/**
 * The people work can be assigned to. Shared across every project, which is why
 * this is a top-level section rather than something inside one.
 *
 * Archived people are hidden by default and the toggle lives in the URL, so a
 * "who used to work on this" view can be shared, matching how the projects list
 * keeps its status filter.
 */
export function Assignees() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [loading, setLoading] = useState(true);
  /** Split for the reason the tasks page spells out: a failed star still has a
   *  list, and replacing it with a red sentence takes away the row you were on. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** The row whose star is in flight, so only that one disables itself. */
  const [starring, setStarring] = useState<string | null>(null);
  const { toast } = useToast();

  const showArchived = searchParams.get('archived') === '1';

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    listAssignees({ includeArchived: showArchived }, controller.signal)
      .then((data) => {
        setAssignees(data.assignees);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load assignees.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [showArchived]);

  /**
   * Move the star, or take it off.
   *
   * The server clears whoever held it before, so the whole list is rewritten
   * from one response rather than re-fetched -- the reply only carries the row
   * that changed, and the person losing the star has to lose it here too.
   */
  async function toggleStar(assignee: Assignee) {
    const next = !assignee.isDefault;
    setStarring(assignee.id);
    try {
      await updateAssignee(assignee.id, { isDefault: next });
      setAssignees((current) =>
        current.map((entry) => ({
          ...entry,
          isDefault: next && entry.id === assignee.id,
        })),
      );
      setActionError(null);
      // Worth saying out loud because starring one person silently un-stars
      // whoever held it before, possibly off-screen.
      toast(
        next
          ? `New tasks you create will be owned by ${assignee.name}`
          : `New tasks you create will be unassigned`,
      );
    } catch (saveError: unknown) {
      setActionError(saveError instanceof Error ? saveError.message : 'Could not set the default.');
    } finally {
      setStarring(null);
    }
  }

  function toggleArchived() {
    const next = new URLSearchParams(searchParams);
    if (showArchived) next.delete('archived');
    else next.set('archived', '1');
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <PageHeader
        title="Assignees"
        meta={!loading && !loadError ? <CountBadge>{assignees.length}</CountBadge> : null}
        description="People you can assign work to. They are not Hive accounts and cannot sign in — adding someone here grants no access. Star yourself and new tasks you create start out owned by you."
        actions={
          <LinkButton variant="primary" to="/assignees/new">
            <PlusIcon />
            New assignee
          </LinkButton>
        }
      />

      <div className="mt-6 flex items-center gap-3">
        <Button aria-pressed={showArchived} onClick={toggleArchived}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </div>

      {/* Above the list, never instead of it -- see `actionError`. */}
      {actionError ? (
        <div className="mt-4">
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      <div className="mt-4">
        {loadError ? (
          <Alert>{loadError}</Alert>
        ) : loading ? (
          <SkeletonList rows={5} />
        ) : assignees.length === 0 ? (
          <EmptyState
            icon={<PeopleIcon className="h-5 w-5" />}
            title="Nobody here yet"
            action={
              <LinkButton variant="primary" to="/assignees/new">
                <PlusIcon />
                Add the first person
              </LinkButton>
            }
          >
            Add the people you want to assign work to.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            {assignees.map((assignee) => (
              <li
                key={assignee.id}
                className="group relative flex items-center gap-3 px-3 py-3 transition hover:bg-stone-100/70"
              >
                <Avatar
                  label={assignee.name}
                  className={assignee.archived ? 'h-7 w-7 opacity-50' : 'h-7 w-7'}
                />

                <div className="min-w-0 flex-1">
                  <Link
                    to={`/assignees/${assignee.id}`}
                    className="block truncate text-sm font-medium text-stone-900 after:absolute after:inset-0"
                  >
                    {assignee.name}
                  </Link>
                  {assignee.email ? (
                    <span className="block truncate text-xs text-stone-500">{assignee.email}</span>
                  ) : null}
                </div>

                {assignee.role ? (
                  <span className="hidden shrink-0 text-xs text-stone-500 sm:block">
                    {assignee.role}
                  </span>
                ) : null}

                {/*
                  Fixed tracks, not a plain flex row.
                  The star and the "Archived" badge are mutually exclusive and
                  only one of them is ever present, so laid out inline the task
                  count landed in a different place on every other row and the
                  right-hand edge of the list read as ragged. Each cell now
                  holds its width whether or not it has anything in it -- the
                  same reasoning TASK_GRID sets out at length.
                */}
                <span className="grid shrink-0 grid-cols-[1.75rem_5rem_4rem] items-center justify-items-end gap-1">
                  <span>
                    {/* Archived people are out of every picker, so there is
                        nothing a star on one could do. */}
                    {assignee.archived ? null : (
                      <StarButton
                        starred={assignee.isDefault}
                        busy={starring === assignee.id}
                        onClick={() => void toggleStar(assignee)}
                      />
                    )}
                  </span>

                  <span>
                    {assignee.archived ? (
                      <Badge className="bg-stone-100 text-stone-500">Archived</Badge>
                    ) : null}
                  </span>

                  <span className="text-xs tabular-nums text-stone-500">
                    {assignee.taskCount === 1 ? '1 task' : `${assignee.taskCount ?? 0} tasks`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
